import { useState } from "react"
import { shellControlClass } from "@/components/layout/shellControl";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { useURLState } from "@/hooks/useURLState";
import { addFilter } from "@/lib/routing/filter-dsl";
import type { FilterOperator } from "@/lib/routing/types";
import { HugeiconsIcon } from "@hugeicons/react"
import {
  AlertCircleIcon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  BrowserIcon,
  EarthIcon,
  FilterIcon,
  GridIcon,
  Link01Icon,
  Location01Icon,
  Megaphone01Icon,
  Mouse01Icon,
  SmartPhone01Icon,
} from "@hugeicons/core-free-icons"

/**
 * FilterDropdown Component - Phase 4
 *
 * Uses new IconFilter1 DSL format (field:operator:value)
 * Maps old categories to new field names
 */

interface FilterOption {
  field: string;
  label: string;
  values: string[];
}

interface FilterDropdownProps {
  dashboardData?: any;
  optionsData?: any;
  showLabel?: boolean;
}

export default function FilterDropdown({ dashboardData, optionsData, showLabel = false }: FilterDropdownProps) {
  const { state, updateState } = useURLState();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"include" | "exclude">("include");

  // Extract available filter options from dashboard data, organized by category
  const getFilterOptions = () => {
    // New shape: comes from the dedicated /filters endpoint via fetchFilterOptionsData
    const fo = (optionsData ?? dashboardData)?.filterOptions;
    if (fo) {
      const pageValues = (fo.pages?.all || []).slice(0, 50) as string[];
      const deviceOs = (fo.devices?.os || []).slice(0, 50) as string[];
      const deviceTypes = (fo.devices?.types || []) as string[];
      return {
        pages: {
          page:      { values: pageValues, hasData: pageValues.length > 0 },
          entryPage: { values: pageValues, hasData: pageValues.length > 0 },
          exitPage:  { values: pageValues, hasData: pageValues.length > 0 },
        },
        deviceBrowser: {
          device:     { values: deviceOs, hasData: deviceOs.length > 0 },
          deviceType: { values: deviceTypes, hasData: true, alwaysShow: true },
          browser:    { values: (fo.browsers || []).slice(0, 50) as string[], hasData: (fo.browsers || []).length > 0 },
        },
        trafficSources: {
          location: { values: (fo.countries  || []).slice(0, 50) as string[], hasData: (fo.countries  || []).length > 0 },
          region:   { values: (fo.regions    || []).slice(0, 50) as string[], hasData: (fo.regions    || []).length > 0 },
          referral: { values: (fo.referrers  || []).slice(0, 50) as string[], hasData: (fo.referrers  || []).length > 0 },
          source:   { values: (fo.utm?.sources   || []) as string[], hasData: (fo.utm?.sources   || []).length > 0 },
          campaign: { values: (fo.utm?.campaigns || []) as string[], hasData: (fo.utm?.campaigns || []).length > 0 },
          medium:   { values: (fo.utm?.mediums   || []) as string[], hasData: (fo.utm?.mediums   || []).length > 0 },
          content:  { values: (fo.utm?.contents  || []) as string[], hasData: (fo.utm?.contents  || []).length > 0 },
          id:       { values: (fo.utm?.ids       || []) as string[], hasData: (fo.utm?.ids       || []).length > 0 },
          term:     { values: (fo.utm?.terms     || []) as string[], hasData: (fo.utm?.terms     || []).length > 0 },
        },
      };
    }

    // Legacy fallback: reads from full metrics shape (dashboardData)
    const metrics = (optionsData ?? dashboardData)?.metrics || {};

    // Pages category
    const pages = metrics.pages?.pageSessions || [];
    const pageValues = pages.slice(0, 50).map((p: any) => p.page);
    const entryPages = pages.filter((p: any) => p.is_entry_page).slice(0, 50).map((p: any) => p.page);
    const exitPages = pages.filter((p: any) => p.is_exit_page).slice(0, 50).map((p: any) => p.page);

    // Device & Browser category
    const devices = metrics.aggregate?.filter((a: any) => a.client_os_aggregate) || [];
    const uniqueDevices = [...new Set(devices.map((d: any) => d.client_os))];
    const mappedDevices = uniqueDevices.map((d) => {
      const lower = String(d).toLowerCase();
      if (lower.includes("windows") || lower.includes("mac") || lower.includes("linux")) return "desktop";
      if (lower.includes("ios") || lower.includes("iphone") || lower.includes("android")) return "mobile";
      return lower;
    });
    const deviceValues = [...new Set(mappedDevices)];

    const browsers = metrics.aggregate?.filter((a: any) => a.client_browser_aggregate) || [];
    const browserValues = [...new Set(browsers.map((b: any) => String(b.client_browser)).filter(Boolean))].slice(0, 50);

    // Traffic Sources category
    const referrals = metrics.aggregate?.filter((a: any) => a.referer_group_aggregate && !a.utm_medium) || [];
    const referralValues = [...new Set(referrals.map((r: any) => r.referer_group))].slice(0, 50);

    const locations = metrics.aggregate?.filter((a: any) => a.country && !a.city && !a.region) || [];
    const locationValues = [...new Set(locations.map((l: any) => l.country))].slice(0, 50);

    const regions = metrics.aggregate?.filter((a: any) => a.region) || [];
    const regionValues = [...new Set(regions.map((r: any) => r.region).filter((r: string) => r && r !== 'unknown'))].slice(0, 50);

    const utmResults = metrics.utmResults || [];
    const utmSourceValues = [...new Set(utmResults.map((u: any) => u.utm_source).filter(Boolean))].slice(0, 50);
    const utmCampaignValues = [...new Set(utmResults.map((u: any) => u.utm_campaign).filter(Boolean))].slice(0, 50);
    const utmMediumValues = [...new Set(utmResults.map((u: any) => u.utm_medium).filter(Boolean))].slice(0, 50);
    const utmContentValues = [...new Set(utmResults.map((u: any) => u.utm_content).filter(Boolean))].slice(0, 50);
    const utmIdValues = [...new Set(utmResults.map((u: any) => u.utm_id).filter(Boolean))].slice(0, 50);
    const utmTermValues = [...new Set(utmResults.map((u: any) => u.utm_term).filter(Boolean))].slice(0, 50);

    return {
      pages: {
        page: { values: pageValues as string[], hasData: pageValues.length > 0 },
        entryPage: { values: entryPages as string[], hasData: entryPages.length > 0 },
        exitPage: { values: exitPages as string[], hasData: exitPages.length > 0 },
      },
      deviceBrowser: {
        device: { values: uniqueDevices.slice(0, 50) as string[], hasData: uniqueDevices.length > 0 },
        deviceType: { values: deviceValues as string[], hasData: deviceValues.length > 0, alwaysShow: true },
        browser: { values: browserValues as string[], hasData: browserValues.length > 0 },
      },
      trafficSources: {
        location: { values: locationValues as string[], hasData: locationValues.length > 0 },
        region: { values: regionValues as string[], hasData: regionValues.length > 0 },
        referral: { values: referralValues as string[], hasData: referralValues.length > 0 },
        source: { values: utmSourceValues as string[], hasData: utmSourceValues.length > 0 },
        campaign: { values: utmCampaignValues as string[], hasData: utmCampaignValues.length > 0 },
        medium: { values: utmMediumValues as string[], hasData: utmMediumValues.length > 0 },
        content: { values: utmContentValues as string[], hasData: utmContentValues.length > 0 },
        id: { values: utmIdValues as string[], hasData: utmIdValues.length > 0 },
        term: { values: utmTermValues as string[], hasData: utmTermValues.length > 0 },
      },
    };
  };

  const filterOptions = getFilterOptions();

  const handleAddFilter = (field: string, value: string) => {
    const operator: FilterOperator = mode === "exclude" ? "!=" : ":";
    const newFilters = addFilter(state.filters, field, operator, value);
    updateState({ filters: newFilters });
    setOpen(false);
  };

  const hasAnyOptions =
    filterOptions.pages.page.hasData ||
    filterOptions.pages.entryPage.hasData ||
    filterOptions.pages.exitPage.hasData ||
    filterOptions.deviceBrowser.device.hasData ||
    filterOptions.deviceBrowser.deviceType.hasData ||
    filterOptions.deviceBrowser.deviceType.alwaysShow ||
    filterOptions.deviceBrowser.browser.hasData ||
    filterOptions.trafficSources.location.hasData ||
    filterOptions.trafficSources.region.hasData ||
    filterOptions.trafficSources.referral.hasData ||
    filterOptions.trafficSources.source.hasData ||
    filterOptions.trafficSources.campaign.hasData ||
    filterOptions.trafficSources.medium.hasData ||
    filterOptions.trafficSources.content.hasData ||
    filterOptions.trafficSources.id.hasData ||
    filterOptions.trafficSources.term.hasData;

  return (
    <DropdownMenu open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) setMode("include"); }}>
      <DropdownMenuTrigger asChild>
        <button type="button" className={cn(shellControlClass, "shrink-0")} aria-label="Filter">
          <HugeiconsIcon icon={FilterIcon} size={13} />
          <span className={showLabel ? "hidden sm:inline" : "sr-only"}>Filter</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-64 max-h-[500px] overflow-y-auto"
      >
        {/* Include / Exclude Toggle */}
        <div className="px-2 py-1.5 mb-1">
          <div className="flex items-center gap-0.5 p-0.5 rounded-md bg-muted/50 border border-border/30">
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMode("include"); }}
              className={`flex-1 px-2.5 py-1 text-xs font-medium rounded transition-all ${
                mode === "include"
                  ? "bg-card text-foreground shadow-sm border border-border/30"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Include
            </button>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMode("exclude"); }}
              className={`flex-1 px-2.5 py-1 text-xs font-medium rounded transition-all ${
                mode === "exclude"
                  ? "bg-destructive/10 text-destructive shadow-sm border border-destructive/20"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Exclude
            </button>
          </div>
        </div>
        <DropdownMenuSeparator />

        {/* Pages Section */}
        {(filterOptions.pages.page.hasData || filterOptions.pages.entryPage.hasData || filterOptions.pages.exitPage.hasData) && (
          <>
            <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground px-2">
              Pages
            </DropdownMenuLabel>
            {filterOptions.pages.page.hasData && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <HugeiconsIcon icon={Mouse01Icon} size={14} className="mr-2" />
                  <span>Page</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto">
                  {filterOptions.pages.page.values.map((value: string) => (
                    <DropdownMenuItem
                      key={value}
                      onClick={() => handleAddFilter("page", value)}
                      className="cursor-pointer"
                    >
                      <span className="truncate max-w-[200px]" title={value}>
                        {value}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            {filterOptions.pages.entryPage.hasData && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <HugeiconsIcon icon={ArrowRight01Icon} size={14} className="mr-2" />
                  <span>Entry page</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto">
                  {filterOptions.pages.entryPage.values.map((value: string) => (
                    <DropdownMenuItem
                      key={value}
                      onClick={() => handleAddFilter("entry_page", value)}
                    >
                      <span className="truncate max-w-[200px]" title={value}>
                        {value}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            {filterOptions.pages.exitPage.hasData && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <HugeiconsIcon icon={ArrowLeft01Icon} size={14} className="mr-2" />
                  <span>Exit page</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto">
                  {filterOptions.pages.exitPage.values.map((value: string) => (
                    <DropdownMenuItem
                      key={value}
                      onClick={() => handleAddFilter("exit_page", value)}
                    >
                      <span className="truncate max-w-[200px]" title={value}>
                        {value}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            <DropdownMenuSeparator />
          </>
        )}

        {/* Device & Browser Section */}
        {(filterOptions.deviceBrowser.device.hasData || filterOptions.deviceBrowser.deviceType.hasData || filterOptions.deviceBrowser.deviceType.alwaysShow || filterOptions.deviceBrowser.browser.hasData) && (
          <>
            <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground px-2">
              Device & Browser
            </DropdownMenuLabel>
            {filterOptions.deviceBrowser.device.hasData && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <HugeiconsIcon icon={BrowserIcon} size={14} className="mr-2" />
                  <span>Device</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto">
                  {filterOptions.deviceBrowser.device.values.map((value: string) => (
                    <DropdownMenuItem
                      key={value}
                      onClick={() => handleAddFilter("os", value)}
                    >
                      <span className="truncate max-w-[200px]" title={value}>
                        {value}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            {(filterOptions.deviceBrowser.deviceType.hasData || filterOptions.deviceBrowser.deviceType.alwaysShow) && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <HugeiconsIcon icon={SmartPhone01Icon} size={14} className="mr-2" />
                  <span>Device type</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto">
                  {["desktop", "mobile"].map((value) => (
                    <DropdownMenuItem
                      key={value}
                      onClick={() => handleAddFilter("device_type", value)}
                    >
                      <span className="capitalize">{value}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            {filterOptions.deviceBrowser.browser.hasData && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <HugeiconsIcon icon={EarthIcon} size={14} className="mr-2" />
                  <span>Browser</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto">
                  {filterOptions.deviceBrowser.browser.values.map((value: string) => (
                    <DropdownMenuItem
                      key={value}
                      onClick={() => handleAddFilter("browser", value)}
                    >
                      <span className="truncate max-w-[200px]" title={value}>
                        {value}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            <DropdownMenuSeparator />
          </>
        )}

        {/* Traffic Sources Section */}
        {(filterOptions.trafficSources.location.hasData ||
          filterOptions.trafficSources.referral.hasData ||
          filterOptions.trafficSources.source.hasData ||
          filterOptions.trafficSources.campaign.hasData ||
          filterOptions.trafficSources.medium.hasData ||
          filterOptions.trafficSources.content.hasData ||
          filterOptions.trafficSources.id.hasData ||
          filterOptions.trafficSources.term.hasData) && (
          <>
            <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground px-2">
              Traffic sources
            </DropdownMenuLabel>
            {filterOptions.trafficSources.location.hasData && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <HugeiconsIcon icon={Location01Icon} size={14} className="mr-2" />
                  <span>Country</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto">
                  {filterOptions.trafficSources.location.values.map((value: string) => (
                    <DropdownMenuItem
                      key={value}
                      onClick={() => handleAddFilter("country", value)}
                    >
                      <span className="truncate max-w-[200px]" title={value}>
                        {value}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            {filterOptions.trafficSources.region.hasData && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <HugeiconsIcon icon={Location01Icon} size={14} className="mr-2" />
                  <span>Region</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto">
                  {filterOptions.trafficSources.region.values.map((value: string) => (
                    <DropdownMenuItem
                      key={value}
                      onClick={() => handleAddFilter("region", value)}
                    >
                      <span className="truncate max-w-[200px]" title={value}>
                        {value}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            {filterOptions.trafficSources.referral.hasData && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <HugeiconsIcon icon={Link01Icon} size={14} className="mr-2" />
                  <span>Referral</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto">
                  {filterOptions.trafficSources.referral.values.map((value: string) => (
                    <DropdownMenuItem
                      key={value}
                      onClick={() => handleAddFilter("referrer", value)}
                    >
                      <span className="truncate max-w-[200px]" title={value}>
                        {value}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            {filterOptions.trafficSources.source.hasData && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <HugeiconsIcon icon={GridIcon} size={14} className="mr-2" />
                  <span>Source</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto">
                  {filterOptions.trafficSources.source.values.map((value: string) => (
                    <DropdownMenuItem
                      key={value}
                      onClick={() => handleAddFilter("utm_source", value)}
                    >
                      <span className="truncate max-w-[200px]" title={value}>
                        {value}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            {filterOptions.trafficSources.campaign.hasData && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <HugeiconsIcon icon={Megaphone01Icon} size={14} className="mr-2" />
                  <span>Campaign</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto">
                  {filterOptions.trafficSources.campaign.values.map((value: string) => (
                    <DropdownMenuItem
                      key={value}
                      onClick={() => handleAddFilter("utm_campaign", value)}
                    >
                      <span className="truncate max-w-[200px]" title={value}>
                        {value}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            {filterOptions.trafficSources.medium.hasData && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <HugeiconsIcon icon={AlertCircleIcon} size={14} className="mr-2" />
                  <span>Medium</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto">
                  {filterOptions.trafficSources.medium.values.map((value: string) => (
                    <DropdownMenuItem
                      key={value}
                      onClick={() => handleAddFilter("utm_medium", value)}
                    >
                      <span className="truncate max-w-[200px]" title={value}>
                        {value}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            {filterOptions.trafficSources.content.hasData && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <HugeiconsIcon icon={AlertCircleIcon} size={14} className="mr-2" />
                  <span>Content</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto">
                  {filterOptions.trafficSources.content.values.map((value: string) => (
                    <DropdownMenuItem
                      key={value}
                      onClick={() => handleAddFilter("utm_content", value)}
                    >
                      <span className="truncate max-w-[200px]" title={value}>
                        {value}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            {filterOptions.trafficSources.id.hasData && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                  <HugeiconsIcon icon={GridIcon} size={14} className="mr-2" />
                  <span>ID</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto">
                  {filterOptions.trafficSources.id.values.map((value: string) => (
                  <DropdownMenuItem
                    key={value}
                      onClick={() => handleAddFilter("utm_id", value)}
                  >
                    <span className="truncate max-w-[200px]" title={value}>
                      {value}
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            )}
            {filterOptions.trafficSources.term.hasData && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <HugeiconsIcon icon={GridIcon} size={14} className="mr-2" />
                  <span>Term</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto">
                  {filterOptions.trafficSources.term.values.map((value: string) => (
                    <DropdownMenuItem
                      key={value}
                      onClick={() => handleAddFilter("utm_term", value)}
                    >
                      <span className="truncate max-w-[200px]" title={value}>
                        {value}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
